import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Card, Badge, Alert, Modal, Toast, ToastContainer, Dropdown } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetNotificationHistory,
  useGetNotificationTemplates,
  sendNotificationEmail,
  sendNotificationSms,
  NotificationHistoryItem,
  GetNotificationHistoryParams,
} from '../../../services/notificationHistoryService';
import { useGetCustomers } from '../../../services/customerService';
import { formatDateTime } from '../../../utils/formatters';
// Reuse the exact Actions dropdown styling from the Loan Customers page
// (colored dropdown items, actions-menu z-index, and the DataTable row
// overflow:visible rule the portalled dropdown depends on).
import '../customers/LoanCustomers.css';
// Anti-flash rule for the Actions dropdown (hide until Popper positions it).
import './communication-actions.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Sent emails embed the logo as an attachment (<img src="cid:tirheloLogo">), which mail clients
// resolve but a browser cannot, so the stored HTML shows a broken image here. Point those
// references at the same logo served by the portal.
const PREVIEW_CID_IMAGES: Record<string, string> = {
  tirheloLogo: `${process.env.PUBLIC_URL}/images/tirhelo-email-logo.png`,
};

const withPreviewImages = (html: string) =>
  html.replace(/(["'])cid:([^"']+)\1/g, (match, quote, id) =>
    PREVIEW_CID_IMAGES[id] ? `${quote}${PREVIEW_CID_IMAGES[id]}${quote}` : match);

const METHOD_CONFIG: Record<string, { label: string; icon: string; bg: string; color: string }> = {
  EMAIL:    { label: 'Email',     icon: 'fa-envelope', bg: '#e3f2fd', color: '#1565c0' },
  SMS:      { label: 'SMS',       icon: 'fa-mobile',   bg: '#e8f5e9', color: '#2e7d32' },
  WHATSAPP: { label: 'WhatsApp',  icon: 'fa-comment',  bg: '#f3e5f5', color: '#6a1b9a' },
};

const STATUS_CONFIG: Record<string, { variant: string; icon: string }> = {
  SENT:    { variant: 'success', icon: 'fa-check-circle' },
  FAILED:  { variant: 'danger',  icon: 'fa-times-circle' },
  PENDING: { variant: 'warning', icon: 'fa-clock-o' },
};

const TEMPLATE_LABELS: Record<string, string> = {
  LOAN_APPROVAL_EMAIL:    'Loan Approval',
  LOAN_APPROVAL_SMS:      'Loan Approval',
  LOAN_RECEIVED_EMAIL:    'Application Received',
  LOAN_RECEIVED_SMS:      'Application Received',
  LOAN_REJECTED_EMAIL:    'Loan Rejected',
  LOAN_REJECTED_SMS:      'Loan Rejected',
  ACCOUNT_OVERDUE_EMAIL:  'Account Overdue',
  ACCOUNT_OVERDUE_SMS:    'Account Overdue',
  PAYMENT_REMINDER_EMAIL: 'Payment Reminder',
  PAYMENT_REMINDER_SMS:   'Payment Reminder',
};

const AVAILABLE_TEMPLATES = [
  { key: 'LOAN_APPROVAL_EMAIL',    label: 'Loan Approval (Email)',        method: 'EMAIL' },
  { key: 'LOAN_APPROVAL_SMS',      label: 'Loan Approval (SMS)',          method: 'SMS'   },
  { key: 'LOAN_RECEIVED_EMAIL',    label: 'Application Received (Email)', method: 'EMAIL' },
  { key: 'LOAN_RECEIVED_SMS',      label: 'Application Received (SMS)',   method: 'SMS'   },
  { key: 'LOAN_REJECTED_EMAIL',    label: 'Loan Rejected (Email)',        method: 'EMAIL' },
  { key: 'LOAN_REJECTED_SMS',      label: 'Loan Rejected (SMS)',          method: 'SMS'   },
  { key: 'ACCOUNT_OVERDUE_EMAIL',  label: 'Account Overdue (Email)',      method: 'EMAIL' },
  { key: 'ACCOUNT_OVERDUE_SMS',    label: 'Account Overdue (SMS)',        method: 'SMS'   },
  { key: 'PAYMENT_REMINDER_EMAIL', label: 'Payment Reminder (Email)',     method: 'EMAIL' },
  { key: 'PAYMENT_REMINDER_SMS',   label: 'Payment Reminder (SMS)',       method: 'SMS'   },
];

interface CustomerOption { customerNumber: string; registeredName: string; }

// Reusable search-as-you-type customer picker. Filters the supplied options and
// shows a dropdown once at least `minChars` characters are typed. Matches on the
// customer number or the registered name depending on `mode`. Used by both the
// Filter panel and the Compose modal so their behaviour is identical.
const CustomerSearchInput: React.FC<{
  label: React.ReactNode;
  placeholder: string;
  mode: 'number' | 'name';
  query: string;
  options: CustomerOption[];
  minChars?: number;
  onQueryChange: (q: string) => void;
  onSelect: (opt: CustomerOption) => void;
}> = ({ label, placeholder, mode, query, options, minChars = 3, onQueryChange, onSelect }) => {
  const [show, setShow] = useState(false);
  const q = query.trim().toLowerCase();
  const matches = q.length >= minChars
    ? options
        .filter(o => (mode === 'number' ? o.customerNumber : o.registeredName).toLowerCase().includes(q))
        .slice(0, 10)
    : [];

  return (
    <div className="position-relative">
      <Form.Label className="fw-semibold">{label}</Form.Label>
      <Form.Control
        type="text"
        placeholder={placeholder}
        autoComplete="off"
        value={query}
        onChange={e => { onQueryChange(e.target.value); setShow(true); }}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 150)}
      />
      {show && q.length >= minChars && (
        <div className="list-group position-absolute w-100 shadow-sm"
             style={{ zIndex: 1000, maxHeight: '220px', overflowY: 'auto' }}>
          {matches.length > 0 ? matches.map(o => (
            <button
              type="button"
              key={o.customerNumber}
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
              onClick={() => { onSelect(o); setShow(false); }}
            >
              {mode === 'number' ? (
                <>
                  <span className="fw-semibold text-primary">{o.customerNumber}</span>
                  {o.registeredName && <span className="text-muted small ms-2 text-truncate">{o.registeredName}</span>}
                </>
              ) : (
                <>
                  <span className="fw-semibold text-truncate">{o.registeredName || '—'}</span>
                  <span className="text-muted small ms-2">{o.customerNumber}</span>
                </>
              )}
            </button>
          )) : (
            <div className="list-group-item text-muted">No matching customers</div>
          )}
        </div>
      )}
    </div>
  );
};

const Communication: React.FC = () => {
  const queryClient = useQueryClient();

  const [page, setPage]   = useState(0);
  const [size, setSize]   = useState(20);
  const [resetPagination, setResetPagination] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [filterForm, setFilterForm]   = useState<GetNotificationHistoryParams>({
    customerNumber: '', templateKey: '', status: '', method: '', fromDate: '', toDate: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<GetNotificationHistoryParams>({});

  // Filter panel customer search (mirrors the Compose customer picker). Both fields
  // resolve to filterForm.customerNumber, which is what the history API filters on.
  const [filterCustomerQuery, setFilterCustomerQuery] = useState('');
  const [filterNameQuery, setFilterNameQuery]         = useState('');

  const [showCompose, setShowCompose]   = useState(false);
  const [composeForm, setComposeForm]   = useState({ customerNumber: '', templateKey: '' });
  const [composeSending, setComposeSending] = useState(false);

  // Compose customer picker — search-as-you-type by customer number or registered name.
  const [composeCustomerQuery, setComposeCustomerQuery] = useState('');
  const [composeNameQuery, setComposeNameQuery]         = useState('');
  const [showNumberResults, setShowNumberResults]       = useState(false);
  const [showNameResults, setShowNameResults]           = useState(false);

  const [toast, setToast] = useState<{ show: boolean; bg: 'success' | 'danger'; message: string } | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Row selected for the "View Notification" detail modal.
  const [viewRow, setViewRow] = useState<NotificationHistoryItem | null>(null);

  const queryParams: GetNotificationHistoryParams = { ...appliedFilters, page, size };
  const { data, isLoading, error } = useGetNotificationHistory(queryParams);

  // Templates carry the actual email/SMS content (the history record only stores
  // metadata + subject), so we look up the matching template to show the full message.
  const { data: templates, isLoading: templatesLoading } = useGetNotificationTemplates();

  // Notification history only carries the customer number. Fetch customers once and
  // build a customerNumber -> registered name lookup for the Registered Name column.
  const { data: customersData } = useGetCustomers(
    { page: 0, size: 2000 },
    { query: { staleTime: 5 * 60 * 1000 } }
  );
  const registeredNameByNumber = React.useMemo(() => {
    const map = new Map<string, string>();
    (customersData?.content ?? []).forEach((c: any) => {
      const info = c?.info ?? {};
      const name =
        info.registeredName ||
        info.tradingName ||
        info.fullNames ||
        [info.firstName, info.lastName].filter(Boolean).join(' ');
      if (c?.customerNumber && name) map.set(c.customerNumber, name);
    });
    return map;
  }, [customersData]);

  // Flat, de-duplicated list of {customerNumber, registeredName} used by the
  // Compose customer search (both the number and registered-name lookups).
  const customerOptions = React.useMemo(() => {
    const seen = new Set<string>();
    const opts: { customerNumber: string; registeredName: string }[] = [];
    (customersData?.content ?? []).forEach((c: any) => {
      const num = c?.customerNumber;
      if (!num || seen.has(num)) return;
      seen.add(num);
      const info = c?.info ?? {};
      const registeredName =
        info.registeredName ||
        info.tradingName ||
        info.fullNames ||
        [info.firstName, info.lastName].filter(Boolean).join(' ') ||
        '';
      opts.push({ customerNumber: num, registeredName });
    });
    return opts;
  }, [customersData]);

  const { data: emailCountData }  = useGetNotificationHistory({ method: 'EMAIL',  page: 0, size: 1 });
  const { data: smsCountData }    = useGetNotificationHistory({ method: 'SMS',    page: 0, size: 1 });
  const { data: failedCountData } = useGetNotificationHistory({ status: 'FAILED', page: 0, size: 1 });

  const handleApplyFilters = () => {
    const cleaned: GetNotificationHistoryParams = {};
    if (filterForm.customerNumber?.trim()) cleaned.customerNumber = filterForm.customerNumber.trim();
    if (filterForm.templateKey)           cleaned.templateKey  = filterForm.templateKey;
    if (filterForm.status)                cleaned.status       = filterForm.status;
    if (filterForm.method)                cleaned.method       = filterForm.method;
    if (filterForm.fromDate)              cleaned.fromDate     = new Date(filterForm.fromDate).toISOString();
    if (filterForm.toDate)                cleaned.toDate       = new Date(filterForm.toDate + 'T23:59:59').toISOString();
    setAppliedFilters(cleaned);
    setPage(0);
    setResetPagination(p => !p);
  };

  const handleResetFilters = () => {
    setFilterForm({ customerNumber: '', templateKey: '', status: '', method: '', fromDate: '', toDate: '' });
    setFilterCustomerQuery('');
    setFilterNameQuery('');
    setAppliedFilters({});
    setPage(0);
    setResetPagination(p => !p);
  };

  // A filter search result was chosen — set the customer-number filter and mirror
  // the selection into both search fields so they stay in sync.
  const selectFilterCustomer = (opt: CustomerOption) => {
    setFilterForm(p => ({ ...p, customerNumber: opt.customerNumber }));
    setFilterCustomerQuery(opt.customerNumber);
    setFilterNameQuery(opt.registeredName);
  };

  // Reset the compose form + customer search state (on open, close, and after send).
  const resetCompose = () => {
    setComposeForm({ customerNumber: '', templateKey: '' });
    setComposeCustomerQuery('');
    setComposeNameQuery('');
    setShowNumberResults(false);
    setShowNameResults(false);
  };

  // A search result was chosen — lock in the customer number and mirror the
  // selection into both search fields so they stay in sync.
  const selectComposeCustomer = (opt: { customerNumber: string; registeredName: string }) => {
    setComposeForm(p => ({ ...p, customerNumber: opt.customerNumber }));
    setComposeCustomerQuery(opt.customerNumber);
    setComposeNameQuery(opt.registeredName);
    setShowNumberResults(false);
    setShowNameResults(false);
  };

  const handleSendNotification = async () => {
    if (!composeForm.customerNumber.trim() || !composeForm.templateKey) return;
    setComposeSending(true);
    try {
      const template = AVAILABLE_TEMPLATES.find(t => t.key === composeForm.templateKey);
      const body = { customerNumber: composeForm.customerNumber.trim(), templateKey: composeForm.templateKey };
      if (template?.method === 'SMS') {
        await sendNotificationSms(body);
      } else {
        await sendNotificationEmail(body);
      }
      setToast({ show: true, bg: 'success', message: 'Notification sent successfully' });
      setShowCompose(false);
      resetCompose();
      queryClient.invalidateQueries({ queryKey: ['/notification/api/v1/history'] });
    } catch {
      setToast({ show: true, bg: 'danger', message: 'Failed to send notification' });
    } finally {
      setComposeSending(false);
    }
  };

  const handleResend = async (row: NotificationHistoryItem) => {
    setResendingId(row.id);
    try {
      const body = { customerNumber: row.customerNumber, templateKey: row.templateKey };
      if (row.method === 'SMS') {
        await sendNotificationSms(body);
      } else {
        await sendNotificationEmail(body);
      }
      setToast({ show: true, bg: 'success', message: 'Notification resent successfully' });
      queryClient.invalidateQueries({ queryKey: ['/notification/api/v1/history'] });
    } catch {
      setToast({ show: true, bg: 'danger', message: 'Failed to resend notification' });
    } finally {
      setResendingId(null);
    }
  };

  const totalElements = data?.totalElements ?? 0;

  const columns: TableColumn<NotificationHistoryItem>[] = [
    {
      name: 'Channel',
      cell: row => {
        const cfg = METHOD_CONFIG[row.method] ?? METHOD_CONFIG.EMAIL;
        return (
          <span
            className="badge d-inline-flex align-items-center gap-1 px-2 py-2"
            style={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.78rem', borderRadius: 8 }}
          >
            <i className={`fa ${cfg.icon}`}></i>
            {cfg.label}
          </span>
        );
      },
      minWidth: '110px',
      maxWidth: '130px',
    },
    {
      name: 'Customer Number',
      selector: row => row.customerNumber,
      sortable: true,
      cell: row => (
        <div className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-2"
               style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
            <i className="fa fa-hashtag text-primary" style={{ fontSize: '0.8rem', display: 'block' }}></i>
          </div>
          <span className="fw-semibold text-primary">{row.customerNumber}</span>
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: 'Registered Name',
      selector: row => registeredNameByNumber.get(row.customerNumber) ?? '',
      sortable: true,
      cell: row => {
        const name = registeredNameByNumber.get(row.customerNumber);
        return name
          ? <span className="fw-semibold">{name}</span>
          : <span className="text-muted">—</span>;
      },
      minWidth: '200px',
    },
    {
      name: 'Notification Type',
      selector: row => row.templateKey,
      sortable: true,
      cell: row => (
        <span className="fw-semibold">
          {TEMPLATE_LABELS[row.templateKey] ?? row.templateKey}
        </span>
      ),
      minWidth: '190px',
    },
    {
      name: 'Recipient',
      selector: row => row.recipient ?? '',
      cell: row => <span className="text-muted small">{row.recipient || '—'}</span>,
      minWidth: '200px',
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      cell: row => {
        const cfg = STATUS_CONFIG[row.status] ?? STATUS_CONFIG.PENDING;
        return (
          <Badge bg={cfg.variant} className="px-3 py-2">
            <i className={`fa ${cfg.icon} me-1`}></i>
            {row.status}
            {row.status === 'FAILED' && row.errorMessage && (
              <i className="fa fa-info-circle ms-1" title={row.errorMessage}></i>
            )}
          </Badge>
        );
      },
      minWidth: '120px',
    },
    {
      name: 'Sent At',
      selector: row => row.sentAt ?? row.createdAt,
      sortable: true,
      cell: row => (
        <span className="text-muted small">
          {row.sentAt ? formatDateTime(row.sentAt) : <em className="text-muted">—</em>}
        </span>
      ),
      minWidth: '185px',
    },
    {
      name: 'Actions',
      // Plain react-bootstrap Dropdown, exactly like the Collections pages.
      // Default (absolute) Popper positioning anchors the menu to the toggle on the
      // first paint. No renderOnMount / strategy:'fixed' — those made the menu flash
      // at the top of the viewport for a frame before Popper applied its transform.
      cell: row => (
        <Dropdown align="end">
          <Dropdown.Toggle
            variant="light"
            size="sm"
            id={`notif-actions-${row.id}`}
            className="rounded-pill d-flex align-items-center no-caret"
          >
            <span>Actions</span>
            <i className="fa fa-caret-down ms-2"></i>
          </Dropdown.Toggle>
          <Dropdown.Menu className="notif-actions-menu">
            <Dropdown.Item className="action-view" onClick={() => setViewRow(row)}>
              <i className="fa fa-eye me-2"></i>
              View Notification
            </Dropdown.Item>
            <Dropdown.Item
              className="action-accounts"
              disabled={resendingId === row.id}
              onClick={() => handleResend(row)}
            >
              <i className="fa fa-refresh me-2"></i>
              {resendingId === row.id ? 'Resending…' : 'Resend Notification'}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
      minWidth: '150px',
      ignoreRowClick: true,
    },
  ];

  // Compose customer search — auto-search once at least 3 characters are typed.
  const COMPOSE_SEARCH_MIN_CHARS = 3;
  const numberQuery = composeCustomerQuery.trim().toLowerCase();
  const nameQuery   = composeNameQuery.trim().toLowerCase();
  const numberMatches = numberQuery.length >= COMPOSE_SEARCH_MIN_CHARS
    ? customerOptions.filter(o => o.customerNumber.toLowerCase().includes(numberQuery)).slice(0, 10)
    : [];
  const nameMatches = nameQuery.length >= COMPOSE_SEARCH_MIN_CHARS
    ? customerOptions.filter(o => o.registeredName.toLowerCase().includes(nameQuery)).slice(0, 10)
    : [];

  return (
    <>
      {/* Page Header */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-white" to="/"><i className="fa fa-home me-2"></i>Home</Link>
                </li>
                <li className="breadcrumb-item active text-white">Communication</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="fs-2 fw-bold mb-2 text-white">Communication</h1>
              <p className="mb-0 text-white fs-6 opacity-75">
                All system notifications sent to customers — email, SMS and WhatsApp
              </p>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="d-flex gap-3 justify-content-md-end flex-wrap align-items-center">
                <Button
                  variant="light"
                  className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                  style={{ backgroundColor: '#ffffff', color: '#495057', border: '2px solid rgba(255,255,255,0.8)', fontSize: '0.9rem', minWidth: '140px' }}
                  onClick={() => setShowFilters(s => !s)}
                >
                  <i className={`fa ${showFilters ? 'fa-times' : 'fa-filter'} me-2`} style={{ fontSize: '0.85rem' }}></i>
                  {showFilters ? 'Hide Filters' : 'Filters'}
                </Button>
                <Button
                  variant="success"
                  className="shadow-lg px-4 py-2 rounded-pill fw-semibold"
                  style={{ backgroundColor: '#ffffff', color: '#28a745', border: '2px solid #28a745', fontSize: '0.9rem', minWidth: '160px' }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#28a745'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#28a745'; }}
                  onClick={() => { resetCompose(); setShowCompose(true); }}
                >
                  <i className="fa fa-paper-plane me-2" style={{ fontSize: '0.85rem' }}></i>
                  Compose Message
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">

          {/* Summary Cards */}
          <div className="row g-4 mb-4">
            {[
              { label: 'Total Notifications', value: totalElements, icon: 'fa-paper-plane',    color: 'primary', border: 'rgba(0,123,255,0.2)' },
              { label: 'Email',               value: emailCountData?.totalElements  ?? 0, icon: 'fa-envelope',      color: 'info',    border: 'rgba(23,162,184,0.2)' },
              { label: 'SMS',                 value: smsCountData?.totalElements    ?? 0, icon: 'fa-mobile',        color: 'success', border: 'rgba(40,167,69,0.2)'  },
              { label: 'Failed',              value: failedCountData?.totalElements ?? 0, icon: 'fa-times-circle',  color: 'danger',  border: 'rgba(220,53,69,0.2)'  },
            ].map(card => (
              <div key={card.label} className="col-lg-3 col-md-6">
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small mb-1">{card.label}</p>
                        <h3 className={`mb-0 fw-bold text-${card.color}`}>{card.value}</h3>
                      </div>
                      <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: `1px solid ${card.border}` }}>
                        <i className={`fa ${card.icon} fa-2x text-${card.color}`} style={{ display: 'block' }}></i>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="row mb-4">
              <div className="col-12">
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-0 py-3">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="fa fa-filter text-primary me-2"></i>Filter Notifications
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Form>
                      <div className="row g-3">
                        <div className="col-md-3">
                          <Form.Group>
                            <CustomerSearchInput
                              label="Customer Number"
                              placeholder="Type at least 3 characters…"
                              mode="number"
                              query={filterCustomerQuery}
                              options={customerOptions}
                              onQueryChange={q => { setFilterCustomerQuery(q); setFilterForm(p => ({ ...p, customerNumber: '' })); }}
                              onSelect={selectFilterCustomer}
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-3">
                          <Form.Group>
                            <CustomerSearchInput
                              label="Registered Name"
                              placeholder="Type at least 3 characters…"
                              mode="name"
                              query={filterNameQuery}
                              options={customerOptions}
                              onQueryChange={q => { setFilterNameQuery(q); setFilterForm(p => ({ ...p, customerNumber: '' })); }}
                              onSelect={selectFilterCustomer}
                            />
                          </Form.Group>
                        </div>
                        <div className="col-md-3">
                          <Form.Group>
                            <Form.Label className="fw-semibold">Channel</Form.Label>
                            <Form.Select value={filterForm.method}
                              onChange={e => setFilterForm(p => ({ ...p, method: e.target.value }))}>
                              <option value="">All Channels</option>
                              <option value="EMAIL">Email</option>
                              <option value="SMS">SMS</option>
                            </Form.Select>
                          </Form.Group>
                        </div>
                        <div className="col-md-3">
                          <Form.Group>
                            <Form.Label className="fw-semibold">Status</Form.Label>
                            <Form.Select value={filterForm.status}
                              onChange={e => setFilterForm(p => ({ ...p, status: e.target.value }))}>
                              <option value="">All Statuses</option>
                              <option value="SENT">Sent</option>
                              <option value="PENDING">Pending</option>
                              <option value="FAILED">Failed</option>
                            </Form.Select>
                          </Form.Group>
                        </div>
                        <div className="col-md-3">
                          <Form.Group>
                            <Form.Label className="fw-semibold">Notification Type</Form.Label>
                            <Form.Select value={filterForm.templateKey}
                              onChange={e => setFilterForm(p => ({ ...p, templateKey: e.target.value }))}>
                              <option value="">All Types</option>
                              {AVAILABLE_TEMPLATES.map(t => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </div>
                        <div className="col-md-3">
                          <Form.Group>
                            <Form.Label className="fw-semibold">From Date</Form.Label>
                            <Form.Control type="date" value={filterForm.fromDate}
                              onChange={e => setFilterForm(p => ({ ...p, fromDate: e.target.value }))} />
                          </Form.Group>
                        </div>
                        <div className="col-md-3">
                          <Form.Group>
                            <Form.Label className="fw-semibold">To Date</Form.Label>
                            <Form.Control type="date" value={filterForm.toDate}
                              onChange={e => setFilterForm(p => ({ ...p, toDate: e.target.value }))} />
                          </Form.Group>
                        </div>
                        <div className="col-12 d-flex justify-content-end gap-2">
                          <Button variant="outline-secondary" onClick={handleResetFilters}>
                            <i className="fa fa-undo me-2"></i>Reset
                          </Button>
                          <Button variant="primary" onClick={handleApplyFilters}>
                            <i className="fa fa-search me-2"></i>Apply Filters
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}

          {/* Notification Table */}
          <div className="row">
            <div className="col-12">
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="fa fa-inbox text-primary me-2"></i>
                      Notification History
                      {totalElements > 0 && (
                        <Badge bg="primary" className="ms-2">{totalElements}</Badge>
                      )}
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {error && (
                    <Alert variant="danger" className="m-3">
                      <i className="fa fa-exclamation-circle me-2"></i>
                      Failed to load notifications. Please try again.
                    </Alert>
                  )}
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading notifications...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={columns}
                        data={data?.content ?? []}
                        pagination
                        paginationServer
                        paginationTotalRows={totalElements}
                        paginationDefaultPage={page + 1}
                        paginationResetDefaultPage={resetPagination}
                        paginationPerPage={size}
                        paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        onChangePage={p => setPage(p - 1)}
                        onChangeRowsPerPage={s => { setSize(s); setPage(0); }}
                        highlightOnHover
                        striped
                        fixedHeader
                        fixedHeaderScrollHeight="calc(100vh - 420px)"
                        paginationComponentOptions={{ rowsPerPageText: 'Rows per page:', rangeSeparatorText: 'of' }}
                        noDataComponent={
                          <div className="text-center py-5">
                            <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                            <h5 className="text-muted">No Notifications Found</h5>
                            <p className="text-muted mb-0">No notifications match your current filters.</p>
                          </div>
                        }
                        customStyles={{
                          headRow:   { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' } },
                          headCells: { style: { paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem', paddingBottom: '1rem' } },
                          cells:     { style: { paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', fontSize: '0.85rem' } },
                          rows:      { style: { minHeight: '55px', borderBottom: '1px solid #f8f9fa' } },
                        }}
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>

        </div>
      </div>

      {/* Compose Modal */}
      <Modal show={showCompose} onHide={() => !composeSending && setShowCompose(false)} centered backdrop={composeSending ? 'static' : true}>
        <Modal.Header closeButton={!composeSending} className="border-0"
          style={{ background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)' }}>
          <Modal.Title className="fw-bold text-black d-flex align-items-center gap-2">
            <i className="fa fa-paper-plane"></i> Compose Notification
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form>
            {/* Customer Number — search-as-you-type */}
            <Form.Group className="mb-3 position-relative">
              <Form.Label className="fw-semibold">Customer Number <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Type at least 3 characters to search…"
                autoComplete="off"
                value={composeCustomerQuery}
                onChange={e => {
                  setComposeCustomerQuery(e.target.value);
                  setShowNumberResults(true);
                  setComposeForm(p => ({ ...p, customerNumber: '' }));
                }}
                onFocus={() => setShowNumberResults(true)}
                onBlur={() => setTimeout(() => setShowNumberResults(false), 150)}
              />
              {showNumberResults && numberQuery.length >= COMPOSE_SEARCH_MIN_CHARS && (
                <div className="list-group position-absolute w-100 shadow-sm"
                     style={{ zIndex: 5, maxHeight: '220px', overflowY: 'auto' }}>
                  {numberMatches.length > 0 ? numberMatches.map(o => (
                    <button
                      type="button"
                      key={o.customerNumber}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => selectComposeCustomer(o)}
                    >
                      <span className="fw-semibold text-primary">{o.customerNumber}</span>
                      {o.registeredName && <span className="text-muted small ms-2 text-truncate">{o.registeredName}</span>}
                    </button>
                  )) : (
                    <div className="list-group-item text-muted">No matching customers</div>
                  )}
                </div>
              )}
            </Form.Group>

            {/* Registered Name — search-as-you-type */}
            <Form.Group className="mb-3 position-relative">
              <Form.Label className="fw-semibold">Registered Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type at least 3 characters to search by company name…"
                autoComplete="off"
                value={composeNameQuery}
                onChange={e => {
                  setComposeNameQuery(e.target.value);
                  setShowNameResults(true);
                  setComposeForm(p => ({ ...p, customerNumber: '' }));
                }}
                onFocus={() => setShowNameResults(true)}
                onBlur={() => setTimeout(() => setShowNameResults(false), 150)}
              />
              {showNameResults && nameQuery.length >= COMPOSE_SEARCH_MIN_CHARS && (
                <div className="list-group position-absolute w-100 shadow-sm"
                     style={{ zIndex: 5, maxHeight: '220px', overflowY: 'auto' }}>
                  {nameMatches.length > 0 ? nameMatches.map(o => (
                    <button
                      type="button"
                      key={o.customerNumber}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => selectComposeCustomer(o)}
                    >
                      <span className="fw-semibold text-truncate">{o.registeredName || '—'}</span>
                      <span className="text-muted small ms-2">{o.customerNumber}</span>
                    </button>
                  )) : (
                    <div className="list-group-item text-muted">No matching customers</div>
                  )}
                </div>
              )}
            </Form.Group>

            {composeForm.customerNumber && (
              <div className="mb-3">
                <span className="badge d-inline-flex align-items-center"
                      style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32', fontWeight: 600 }}>
                  <i className="fa fa-check-circle me-1"></i>
                  Selected: {composeForm.customerNumber}
                  {registeredNameByNumber.get(composeForm.customerNumber)
                    ? ` — ${registeredNameByNumber.get(composeForm.customerNumber)}`
                    : ''}
                </span>
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Notification Template <span className="text-danger">*</span></Form.Label>
              <Form.Select value={composeForm.templateKey}
                onChange={e => setComposeForm(p => ({ ...p, templateKey: e.target.value }))}>
                <option value="">Select a template…</option>
                {AVAILABLE_TEMPLATES.map(t => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </Form.Select>
              {composeForm.templateKey && (
                <Form.Text className="text-muted">
                  Channel: <strong>{AVAILABLE_TEMPLATES.find(t => t.key === composeForm.templateKey)?.method}</strong>
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSendNotification}
            disabled={composeSending || !composeForm.customerNumber.trim() || !composeForm.templateKey}
            style={{ background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)', border: 'none', color: '#000', fontWeight: 600 }}
          >
            {composeSending ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</>
            ) : (
              <><i className="fa fa-paper-plane me-2"></i>Send</>
            )}
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowCompose(false)} disabled={composeSending}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Notification Modal */}
      <Modal show={!!viewRow} onHide={() => setViewRow(null)} centered size="lg" scrollable>
        <Modal.Header closeButton className="border-0"
          style={{ background: 'linear-gradient(135deg, #00FF00 0%, #00CC00 100%)' }}>
          <Modal.Title className="fw-bold text-black d-flex align-items-center gap-2">
            <i className="fa fa-eye"></i> Notification Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {viewRow && (() => {
            const cfg = METHOD_CONFIG[viewRow.method] ?? METHOD_CONFIG.EMAIL;
            const statusCfg = STATUS_CONFIG[viewRow.status] ?? STATUS_CONFIG.PENDING;
            const template = templates?.find(t => t.key === viewRow.templateKey);
            const isEmail = viewRow.method === 'EMAIL';
            const subjectLine = viewRow.subject || template?.subject || '—';
            const sentAtLabel = viewRow.sentAt ? formatDateTime(viewRow.sentAt) : '—';

            return (
              <>
                {/* Metadata */}
                <div className="row g-3 mb-4">
                  {[
                    { label: 'Channel', node: (
                      <span className="badge d-inline-flex align-items-center gap-1 px-2 py-1"
                        style={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, borderRadius: 8 }}>
                        <i className={`fa ${cfg.icon}`}></i>{cfg.label}
                      </span>
                    ) },
                    { label: 'Status', node: (
                      <Badge bg={statusCfg.variant} className="px-2 py-1">
                        <i className={`fa ${statusCfg.icon} me-1`}></i>{viewRow.status}
                      </Badge>
                    ) },
                    { label: 'Customer', node: <span className="fw-semibold text-primary">{viewRow.customerNumber}</span> },
                    { label: 'Notification Type', node: <span className="fw-semibold">{TEMPLATE_LABELS[viewRow.templateKey] ?? viewRow.templateKey}</span> },
                    { label: 'Recipient', node: <span>{viewRow.recipient || '—'}</span> },
                    { label: 'Sent At', node: <span>{sentAtLabel}</span> },
                  ].map(item => (
                    <div className="col-md-4" key={item.label}>
                      <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: '0.5px' }}>{item.label}</div>
                      <div>{item.node}</div>
                    </div>
                  ))}
                </div>

                {viewRow.status === 'FAILED' && viewRow.errorMessage && (
                  <Alert variant="danger" className="d-flex align-items-start">
                    <i className="fa fa-exclamation-circle me-2 mt-1"></i>
                    <div><strong>Delivery failed:</strong> {viewRow.errorMessage}</div>
                  </Alert>
                )}

                {/* Message content */}
                <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ letterSpacing: '0.5px', fontSize: '0.78rem' }}>
                  <i className={`fa ${isEmail ? 'fa-envelope' : 'fa-mobile'} me-2`}></i>
                  {isEmail ? 'Full Email' : 'Full SMS'}
                </h6>

                {/* Prefer the actual rendered content that was sent to the customer.
                    Fall back to the template only for older records saved before we
                    started persisting the sent content. */}
                {(() => {
                  const actualContent = viewRow.content?.trim() || null;
                  const displayContent = actualContent ?? template?.content ?? null;

                  if (!actualContent && templatesLoading) {
                    return (
                      <div className="text-center py-4">
                        <span className="spinner-border text-primary" role="status"></span>
                        <p className="mt-2 text-muted mb-0">Loading message content…</p>
                      </div>
                    );
                  }

                  if (!displayContent) {
                    return (
                      <Alert variant="warning" className="mb-0">
                        <i className="fa fa-exclamation-triangle me-2"></i>
                        The message content for template <code>{viewRow.templateKey}</code> is no longer available.
                        {viewRow.subject && <div className="mt-2"><strong>Subject:</strong> {viewRow.subject}</div>}
                      </Alert>
                    );
                  }

                  return (
                    <>
                      {isEmail ? (
                        <div className="border rounded overflow-hidden">
                          <div className="p-3 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="mb-1"><span className="text-muted small">From:</span> <span className="fw-semibold">Tirhelo Financial Services</span></div>
                            <div className="mb-1"><span className="text-muted small">To:</span> <span className="fw-semibold">{viewRow.recipient || '—'}</span></div>
                            <div><span className="text-muted small">Subject:</span> <span className="fw-semibold">{subjectLine}</span></div>
                          </div>
                          <div className="p-3 bg-white" style={{ maxHeight: '45vh', overflowY: 'auto' }}
                            dangerouslySetInnerHTML={{ __html: withPreviewImages(displayContent) }} />
                        </div>
                      ) : (
                        <div className="d-flex flex-column align-items-start">
                          <div className="text-muted small mb-2">
                            <i className="fa fa-mobile me-1"></i>To: <span className="fw-semibold">{viewRow.recipient || '—'}</span>
                          </div>
                          <div className="p-3 rounded-4"
                            style={{ backgroundColor: '#e8f5e9', color: '#1b5e20', maxWidth: '520px', whiteSpace: 'pre-wrap', borderTopLeftRadius: 4 }}>
                            {displayContent}
                          </div>
                        </div>
                      )}

                      <p className="text-muted small mt-3 mb-0">
                        <i className="fa fa-info-circle me-1"></i>
                        {actualContent
                          ? (viewRow.status === 'SENT'
                              ? `This is the exact ${isEmail ? 'email' : 'SMS'} that was delivered to the customer.`
                              : `This is the exact ${isEmail ? 'email' : 'SMS'} content that was composed for this notification. Delivery did not succeed — see the status above.`)
                          : `This is the ${isEmail ? 'email' : 'SMS'} template used for this notification; dynamic values (e.g. customer name, amounts) appear as placeholders.`}
                      </p>
                    </>
                  );
                })()}
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          {viewRow && (
            <Button
              variant="outline-success"
              disabled={resendingId === viewRow.id}
              onClick={() => handleResend(viewRow)}
            >
              {resendingId === viewRow.id ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Resending…</>
              ) : (
                <><i className="fa fa-refresh me-2"></i>Resend Notification</>
              )}
            </Button>
          )}
          <Button variant="outline-secondary" onClick={() => setViewRow(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast bg={toast?.bg} show={!!toast?.show} autohide delay={4000} onClose={() => setToast(null)}>
          <Toast.Body className="text-white d-flex align-items-center">
            <i className={`fa ${toast?.bg === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
            <span className="fw-semibold">{toast?.message}</span>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default Communication;
